import React from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore — resolved at build time via Vite aliases
import {
  SunIcon,
  StarIcon,
  MusicIcon,
  MountainCityIcon,
  GuitarIcon,
  CowboyHatIcon,
  SeedlingIcon,
  DoveIcon,
  OilWellIcon,
  ShieldIcon,
  CowIcon,
  HouseIcon,
} from '@vibesites/assets/icons/ui';

function makeLinkSlotDef(label: string, url: string): SlotGroupDefinition {
  return {
    label: `City Link — ${label}`,
    fields: {
      url: { label: 'URL', type: 'url', defaultValue: url, defaultField: '' },
      label: { label: 'Link Text', type: 'text', defaultValue: label, defaultField: '' },
      _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' },
    },
  };
}

const promoLinkDef: SlotGroupDefinition = {
  label: 'Promo Banner Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok', defaultField: '' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Check out our new communities at Cypress Crossing, Summit Villas and Trails at Aspen Creek!', defaultField: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' },
  },
};

const DEFS = {
  'ctasection.city_1':  makeLinkSlotDef('Amarillo, TX',      '/apartments/tx/amarillo/'),
  'ctasection.city_2':  makeLinkSlotDef('El Paso, TX',       '/apartments/tx/el-paso/'),
  'ctasection.city_3':  makeLinkSlotDef('Kansas City, MO',   '/apartments/mo/kansas-city/'),
  'ctasection.city_4':  makeLinkSlotDef('Little Rock, AR',   '/apartments/ar/little-rock/'),
  'ctasection.city_5':  makeLinkSlotDef('Lubbock, TX',       '/apartments/tx/lubbock/'),
  'ctasection.city_6':  makeLinkSlotDef('Oklahoma City, OK', '/apartments/ok/oklahoma-city/'),
  'ctasection.city_7':  makeLinkSlotDef('Pryor, OK',         '/apartments/ok/pryor/'),
  'ctasection.city_8':  makeLinkSlotDef('Ridgeland, MS',     '/apartments/ms/ridgeland/'),
  'ctasection.city_9':  makeLinkSlotDef('Stillwater, OK',    '/apartments/ok/stillwater/'),
  'ctasection.city_10': makeLinkSlotDef('Tulsa, OK',         '/apartments/ok/tulsa/'),
  'ctasection.city_11': makeLinkSlotDef('Waco, TX',          '/apartments/tx/waco/'),
  'ctasection.city_12': makeLinkSlotDef('Wichita, KS',       '/apartments/ks/wichita/'),
} satisfies Record<string, SlotGroupDefinition>;

const ICONS: Record<string, React.ComponentType<any>> = {
  'ctasection.city_1':  SunIcon,
  'ctasection.city_2':  StarIcon,
  'ctasection.city_3':  MusicIcon,
  'ctasection.city_4':  MountainCityIcon,
  'ctasection.city_5':  GuitarIcon,
  'ctasection.city_6':  CowboyHatIcon,
  'ctasection.city_7':  CowboyHatIcon,
  'ctasection.city_8':  SeedlingIcon,
  'ctasection.city_9':  DoveIcon,
  'ctasection.city_10': OilWellIcon,
  'ctasection.city_11': ShieldIcon,
  'ctasection.city_12': CowIcon,
};

function CitySlot({ path }: { path: keyof typeof DEFS }) {
  const def = DEFS[path];
  const slot = useSlotGroup(path, def);
  const Icon = ICONS[path];
  return (
    <a
      href={slot.url || def.fields.url.defaultValue}
      target={slot._target || '_self'}
      rel={slot._target === '_blank' ? 'noopener noreferrer' : undefined}
      className="case-city-link"
    >
      <span className="case-city-link-icon">
        <Icon size={16} color="currentColor" />
      </span>
      <span className="case-city-link-label">{slot.label || def.fields.label.defaultValue}</span>
    </a>
  );
}

export function CTASection() {
  const promoLink = useSlotGroup('ctasection.promo_link', promoLinkDef);

  return (
    <>
      <div className="case-city-banner">
        <div className="case-city-banner-inner">
          <EditableSlotGroup groupPath="ctasection.city_1" definition={DEFS['ctasection.city_1']}>
            <CitySlot path="ctasection.city_1" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_2" definition={DEFS['ctasection.city_2']}>
            <CitySlot path="ctasection.city_2" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_3" definition={DEFS['ctasection.city_3']}>
            <CitySlot path="ctasection.city_3" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_4" definition={DEFS['ctasection.city_4']}>
            <CitySlot path="ctasection.city_4" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_5" definition={DEFS['ctasection.city_5']}>
            <CitySlot path="ctasection.city_5" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_6" definition={DEFS['ctasection.city_6']}>
            <CitySlot path="ctasection.city_6" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_7" definition={DEFS['ctasection.city_7']}>
            <CitySlot path="ctasection.city_7" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_8" definition={DEFS['ctasection.city_8']}>
            <CitySlot path="ctasection.city_8" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_9" definition={DEFS['ctasection.city_9']}>
            <CitySlot path="ctasection.city_9" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_10" definition={DEFS['ctasection.city_10']}>
            <CitySlot path="ctasection.city_10" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_11" definition={DEFS['ctasection.city_11']}>
            <CitySlot path="ctasection.city_11" />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="ctasection.city_12" definition={DEFS['ctasection.city_12']}>
            <CitySlot path="ctasection.city_12" />
          </EditableSlotGroup>
        </div>
      </div>
      <div className="case-promo-banner">
        <div className="case-promo-banner-inner">
          <HouseIcon size={16} color="currentColor" aria-label="New communities" />
          <EditableSlotGroup groupPath="ctasection.promo_link" definition={promoLinkDef}>
            <a
              href={promoLink.url || promoLinkDef.fields.url.defaultValue}
              target={promoLink._target || '_self'}
              className="case-promo-banner-link hover:underline"
            >
              {promoLink.label || promoLinkDef.fields.label.defaultValue}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'City Quick Links',
  tokens: {},
  slotGroups: {
    'ctasection.promo_link': { label: 'Promo Banner Link', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Check out our new communities at Cypress Crossing, Summit Villas and Trails at Aspen Creek!', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_1': { label: 'City Link — Amarillo, TX', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/tx/amarillo/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Amarillo, TX', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_2': { label: 'City Link — El Paso, TX', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/tx/el-paso/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'El Paso, TX', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_3': { label: 'City Link — Kansas City, MO', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/mo/kansas-city/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Kansas City, MO', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_4': { label: 'City Link — Little Rock, AR', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ar/little-rock/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Little Rock, AR', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_5': { label: 'City Link — Lubbock, TX', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/tx/lubbock/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Lubbock, TX', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_6': { label: 'City Link — Oklahoma City, OK', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok/oklahoma-city/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Oklahoma City, OK', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_7': { label: 'City Link — Pryor, OK', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok/pryor/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Pryor, OK', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_8': { label: 'City Link — Ridgeland, MS', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ms/ridgeland/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Ridgeland, MS', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_9': { label: 'City Link — Stillwater, OK', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok/stillwater/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Stillwater, OK', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_10': { label: 'City Link — Tulsa, OK', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ok/tulsa/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Tulsa, OK', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_11': { label: 'City Link — Waco, TX', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/tx/waco/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Waco, TX', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
    'ctasection.city_12': { label: 'City Link — Wichita, KS', fields: { url: { label: 'URL', type: 'url', defaultValue: '/apartments/ks/wichita/', defaultField: '' }, label: { label: 'Link Text', type: 'text', defaultValue: 'Wichita, KS', defaultField: '' }, _target: { label: 'Target', type: 'text', defaultValue: '_self', defaultField: '' } } },
  },
};

(CTASection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CTASection;
